import { useMemo } from 'react'
import { documentAtom, layerAtomFamily, layersAtom, layerTreeAtom, onCommitAtom, selectedLayerIdAtom } from './atoms'
import { useCanvasStore } from './canvas-store'
import type { Layer } from './data/v1'
import type { LayerNode } from './data/v1'

type UpdateOptions = {
	transient?: boolean
}

export function useDesignerActions() {
	const store = useCanvasStore()

	return useMemo(() => {
		function commit() {
			const onCommit = store.get(onCommitAtom)
			if (onCommit) {
				onCommit(store.get(documentAtom))
			}
		}

		function updateLayer(
			id: string,
			changes: Partial<Layer> | ((prev: Layer) => Partial<Layer>),
			opts?: UpdateOptions,
		) {
			store.set(layerAtomFamily(id), (prev) => {
				const resolved = typeof changes === 'function' ? changes(prev) : changes
				return { ...prev, ...resolved } as Layer
			})
			if (!opts?.transient) { commit() }
		}

		function renameLayer(oldId: string, newId: string): boolean {
			const layers = store.get(layersAtom)
			if (newId in layers) { return false // ID already exists
			 }

			const layer = layers[oldId]
			if (!layer) { return false }

			// Update layers dict: add new key, remove old
			store.set(layersAtom, (prev) => {
				const next = { ...prev }
				delete next[oldId]
				next[newId] = { ...layer, id: newId }
				return next
			})

			// Update tree: replace old id with new id
			function updateTreeIds(nodes: LayerNode[]): LayerNode[] {
				return nodes.map(node => ({
					id: node.id === oldId ? newId : node.id,
					children: updateTreeIds(node.children),
				}))
			}
			store.set(layerTreeAtom, (prev) => updateTreeIds(prev))

			// Update selection if needed
			if (store.get(selectedLayerIdAtom) === oldId) {
				store.set(selectedLayerIdAtom, newId)
			}

			commit()
			return true
		}

		function selectLayer(id: string) {
			store.set(selectedLayerIdAtom, id)
		}

		return { updateLayer, renameLayer, selectLayer, commit }
	}, [store])
}
